// Typed Redux hooks — the canonical selector/dispatch for both web apps.
//
// Use these instead of bare `useSelector` / `useDispatch` so components get
// `RootState` inference (no `(state: any)`, no per-file `interface RootState`)
// and a dispatch that accepts the store's async thunks.

import { useDispatch, useSelector, useStore } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";
import type { Store } from "@reduxjs/toolkit";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { RootState } from "./types";

// Hand-authored rather than `typeof store.dispatch`: the store entry is still
// untyped JS, so deriving from it would yield `any`. ThunkDispatch makes the
// async thunks (checkout, loadUserInfoData, …) dispatchable without casts.
export type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

// Aliased with type annotations rather than `useDispatch.withTypes<…>()`:
// `.withTypes` runs at module-evaluation time, so a test that mocks react-redux
// without a `withTypes` method crashes the whole suite at import. Plain aliases
// are identical at runtime and resilient to those mocks.
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
// For reading the latest state inside async work, where a selected value is stale.
export const useAppStore: () => Store<RootState> = useStore;
